import API_BASE from '@/lib/apiBase';
import { http } from '@/lib/http';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { dbg } from '@/lib/dbg';
import { toast } from '@/hooks/use-toast';

// Campaign entity type for list views
export interface Campaign {
  id: number;
  name: string;
  description?: string;
  status: string;
  statusId?: number;
  retailerId: number;
  retailerName?: string;
  startDate?: string;
  endDate?: string;
  isActive?: boolean;
  createdAt: string;
  updatedAt?: string | null;
  cycleId?: number | null;
  cycleName?: string | null;
  advertiserId?: number | null;
  advertiserName?: string | null;
  // Phase 4.7A: Commercial context fields for hierarchical cohesion
  clientId?: number | null;
  clientName?: string | null;
  bookingId?: number | null;
  bookingName?: string | null;
  // Derived read-only field — computed from real-world events, never from status
  flightStatus?: 'sent_to_operators' | 'posting_started' | 'removal_started' | null;
}

// Paginated response type from GET /api/campaigns
export interface CampaignsPaginatedResponse {
  data: Campaign[];
  totalCount: number;
  page: number;
  pageSize: number;
}

// Query params for fetching campaigns
export interface FetchCampaignsParams {
  retailerId?: number | null;
  cycleId?: number | null;
  includeArchived?: boolean;
  page?: number;
  pageSize?: number;
}

export interface UpdateCampaignStatusParams {
  campaignId: number;
  statusId: number;
}

export async function updateCampaignStatus({ campaignId, statusId }: UpdateCampaignStatusParams) {
  dbg.log('[CAMPAIGNS_API] Updating campaign status', { campaignId, statusId });

  const url = `${API_BASE}/api/campaigns/${campaignId}`;
  const res = await http(url, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ statusId }),
    component: 'updateCampaignStatus',
  });

  if (!res.ok) {
    const error = await res.json().catch(() => ({ error: 'Failed to update campaign status' }));
    throw new Error(error.error || `Failed to update campaign status (${res.status})`);
  }

  return await res.json();
}

export function useUpdateCampaignStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateCampaignStatus,
    onMutate: async ({ campaignId, statusId }) => {
      // Cancel any outgoing refetches - use fuzzy matching for paginated query keys
      await queryClient.cancelQueries({ queryKey: ['/api/campaigns'], exact: false });

      // Snapshot the previous value - capture all matching queries
      const previousData = queryClient.getQueriesData({ queryKey: ['/api/campaigns'] });

      // Optimistically update all cached campaign queries (handles paginated response)
      queryClient.setQueriesData({ queryKey: ['/api/campaigns'] }, (old: any) => {
        if (!old) return old;
        // Handle paginated response shape: { data: Campaign[], totalCount, page, pageSize }
        if (old.data && Array.isArray(old.data)) {
          return {
            ...old,
            data: old.data.map((campaign: any) =>
              campaign.id === campaignId
                ? { ...campaign, statusId, status_id: statusId }
                : campaign
            ),
          };
        }
        // Legacy: handle flat array response (backward compatibility during transition)
        if (Array.isArray(old)) {
          return old.map((campaign: any) =>
            campaign.id === campaignId
              ? { ...campaign, statusId, status_id: statusId }
              : campaign
          );
        }
        return old;
      });

      dbg.log('[CAMPAIGNS_API] Optimistic update applied', { campaignId, statusId });

      return { previousData };
    },
    onError: (error, variables, context) => {
      // Revert the optimistic update on error
      if (context?.previousData) {
        context.previousData.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }

      dbg.error('[CAMPAIGNS_API] Failed to update campaign status', error);

      toast({
        title: 'Failed to update status',
        description: error instanceof Error ? error.message : 'An error occurred',
        variant: 'destructive',
      });
    },
    onSuccess: (data, variables) => {
      dbg.log('[CAMPAIGNS_API] Campaign status updated successfully', { campaignId: variables.campaignId });

      // Invalidate and refetch all campaign queries
      queryClient.invalidateQueries({ queryKey: ['/api/campaigns'], exact: false });

      toast({
        title: 'Status updated',
        description: 'Campaign status has been updated successfully.',
      });
    },
  });
}

export interface BulkUpdateCampaignStatusParams {
  campaignIds: number[];
  statusId: number;
}

export interface BulkCampaignStatusFailure {
  campaignId: number;
  campaignName?: string | null;
  error: string;
  code?: string;
}

export interface BulkCampaignStatusResult {
  success: boolean;
  requestedCount: number;
  updatedCount: number;
  failedCount: number;
  updated: Campaign[];
  failed: BulkCampaignStatusFailure[];
}

export async function bulkUpdateCampaignStatus({
  campaignIds,
  statusId,
}: BulkUpdateCampaignStatusParams): Promise<BulkCampaignStatusResult> {
  dbg.log('[CAMPAIGNS_API] Bulk updating campaign status', {
    count: campaignIds.length,
    statusId,
  });

  const url = `${API_BASE}/api/campaigns/bulk-status`;
  const res = await http(url, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ campaignIds, statusId }),
    component: 'bulkUpdateCampaignStatus',
  });

  if (!res.ok) {
    const errorBody = await res.json().catch(() => ({ error: 'Failed to update campaign status' }));
    throw new Error(errorBody.error || `Failed to bulk update campaign status (${res.status})`);
  }

  return (await res.json()) as BulkCampaignStatusResult;
}

export function useBulkUpdateCampaignStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: bulkUpdateCampaignStatus,
    onError: (error) => {
      dbg.error('[CAMPAIGNS_API] Bulk campaign status update failed', error);
      toast({
        title: 'Bulk update failed',
        description: error instanceof Error ? error.message : 'An error occurred',
        variant: 'destructive',
      });
    },
    onSuccess: (data) => {
      dbg.log('[CAMPAIGNS_API] Bulk campaign status update complete', {
        updatedCount: data.updatedCount,
        failedCount: data.failedCount,
      });

      queryClient.invalidateQueries({ queryKey: ['/api/campaigns'], exact: false });

      if (data.failedCount === 0) {
        toast({
          title: 'Campaigns updated',
          description: `${data.updatedCount} campaign${data.updatedCount === 1 ? '' : 's'} updated successfully.`,
        });
      } else if (data.updatedCount === 0) {
        toast({
          title: 'No campaigns updated',
          description: `All ${data.failedCount} selected campaign${data.failedCount === 1 ? '' : 's'} failed to update.`,
          variant: 'destructive',
        });
      } else {
        toast({
          title: 'Bulk update partially complete',
          description: `${data.updatedCount} updated, ${data.failedCount} failed. See details for failed campaigns.`,
        });
      }
    },
  });
}

